// =============================================================================
// AppointmentForm — create/edit appointments. Faithful to IMS Appointment_Add
// (customer, made-date, date/time, designer, pages, comments) plus the
// Appointment_View follow-up fields (outcome, go-ahead, on-the-spot, advise,
// look-at-job/plants, mailed date).
// =============================================================================
import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import {
  listDesigners, listAppointmentOutcomes, createAppointment, updateAppointment,
  listCustomers, type DesignerOption,
} from '../services/data'
import { Card, Button } from './shared/ui'
import { customerLabel } from '../lib/format'
import { toast } from 'react-toastify'
import type { Appointment, Customer } from '../types'

function toDateInput(v: string | null | undefined): string {
  if (!v) return ''
  const d = new Date(v)
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10)
}
function toTimeInput(v: string | null | undefined): string {
  if (!v) return ''
  const d = new Date(v)
  return isNaN(d.getTime()) ? '' : d.toTimeString().slice(0, 5)
}

export default function AppointmentForm({ appointment, customer, onClose, onSaved }: {
  appointment?: Appointment | null
  customer?: Customer | null
  onClose: () => void
  onSaved: (a: Appointment) => void
}) {
  const isEdit = !!appointment
  const [designers, setDesigners] = useState<DesignerOption[]>([])
  const [outcomes, setOutcomes] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  // Customer selection (fixed when editing or launched from a customer).
  const [cust, setCust] = useState<Customer | null>(customer ?? null)
  const [q, setQ] = useState('')
  const [results, setResults] = useState<Customer[]>([])

  const [f, setF] = useState({
    appointment_made_date: appointment?.appointment_made_date ?? new Date().toISOString().slice(0, 10),
    appointment_date: toDateInput(appointment?.appointment_date),
    appointment_time: toTimeInput(appointment?.appointment_time),
    designer_id: appointment?.designer_id ?? '',
    pages: appointment?.pages ?? '',
    comments: appointment?.comments ?? '',
    go_ahead: appointment?.go_ahead ?? false,
    outcome: appointment?.outcome ?? '',
    on_the_spot: appointment?.on_the_spot ?? '',
    advise: appointment?.advise ?? '',
    look_at_job: appointment?.look_at_job ?? '',
    look_at_plants: appointment?.look_at_plants ?? '',
    mailed_date: appointment?.mailed_date ?? '',
  })
  const set = (k: keyof typeof f, v: string | boolean) => setF(p => ({ ...p, [k]: v }))

  useEffect(() => {
    listDesigners().then(setDesigners).catch(() => setDesigners([]))
    listAppointmentOutcomes().then(setOutcomes).catch(() => setOutcomes([]))
  }, [])

  useEffect(() => {
    if (cust || !q.trim()) { setResults([]); return }
    let active = true
    listCustomers({ search: q, pageSize: 8 }).then(r => { if (active) setResults(r.rows) }).catch(() => {})
    return () => { active = false }
  }, [q, cust])

  const save = async () => {
    if (!cust) { toast.error('Select a customer'); return }
    if (!f.appointment_date) { toast.error('Pick an appointment date'); return }
    setSaving(true)
    try {
      const apptTime = f.appointment_time
        ? new Date(`${f.appointment_date}T${f.appointment_time}`).toISOString()
        : null
      const payload: Partial<Appointment> = {
        customer_id: cust.id,
        appointment_made_date: f.appointment_made_date || null,
        appointment_date: f.appointment_date,
        appointment_time: apptTime,
        designer_id: f.designer_id || null,
        pages: f.pages || null,
        comments: f.comments || null,
        go_ahead: f.go_ahead,
        outcome: f.outcome || null,
        on_the_spot: f.on_the_spot || null,
        advise: f.advise || null,
        look_at_job: f.look_at_job || null,
        look_at_plants: f.look_at_plants || null,
        mailed_date: f.mailed_date || null,
      }
      const saved = isEdit
        ? await updateAppointment(appointment!.id, payload)
        : await createAppointment(payload)
      toast.success(isEdit ? 'Appointment updated' : 'Appointment created')
      onSaved(saved)
    } catch (e) { console.error(e); toast.error('Failed to save appointment') } finally { setSaving(false) }
  }

  const field = (label: string, k: keyof typeof f, type = 'text', cls = '') => (
    <div className={cls}>
      <label className="text-xs font-medium text-gray-500">{label}</label>
      <input type={type} value={f[k] as string} onChange={e => set(k, e.target.value)}
        className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">{isEdit ? 'Edit Appointment' : 'New Appointment'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          {/* Customer */}
          <div>
            <label className="text-xs font-medium text-gray-500">Customer</label>
            {cust ? (
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm mt-1">
                <span><b>{customerLabel(cust)}</b>{cust.company_name ? ` · ${cust.company_name}` : ''}{cust.city ? ` · ${cust.city}` : ''}</span>
                {!isEdit && !customer && <button onClick={() => setCust(null)} className="text-green-700 hover:underline text-xs">change</button>}
              </div>
            ) : (
              <div className="relative mt-1">
                <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search a customer…"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                {results.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                    {results.map(c => (
                      <button key={c.id} onClick={() => { setCust(c); setResults([]); setQ('') }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-50">
                        <span className="font-medium">{customerLabel(c)}</span>
                        {c.company_name && <span className="text-gray-500"> · {c.company_name}</span>}
                        {c.city && <span className="text-xs text-gray-400 block">{c.city}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {field('Appointment Made', 'appointment_made_date')}
            {field('Date', 'appointment_date', 'date')}
            {field('Time', 'appointment_time', 'time')}
            <div>
              <label className="text-xs font-medium text-gray-500">Designer</label>
              <select value={f.designer_id} onChange={e => set('designer_id', e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">— None —</option>
                {designers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            {field('Pages', 'pages')}
            <div>
              <label className="text-xs font-medium text-gray-500">Outcome</label>
              <select value={f.outcome} onChange={e => set('outcome', e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">—</option>
                {outcomes.map(o => <option key={o} value={o}>{o}</option>)}
                {f.outcome && !outcomes.includes(f.outcome) && <option value={f.outcome}>{f.outcome}</option>}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {field('On The Spot', 'on_the_spot')}
            {field('Advise', 'advise')}
            {field('Look At Job', 'look_at_job')}
            {field('Look At Plants', 'look_at_plants')}
            {field('Mailed Date', 'mailed_date')}
            <label className="flex items-center gap-2 mt-6 text-sm text-gray-700">
              <input type="checkbox" checked={f.go_ahead} onChange={e => set('go_ahead', e.target.checked)} /> Go Ahead
            </label>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500">Comments</label>
            <textarea value={f.comments} onChange={e => set('comments', e.target.value)} rows={3}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </Card>
    </div>
  )
}
